import { Typography } from '@mui/joy';
import { CalendarBlank } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { StatusCodes } from 'http-status-codes';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { axiosInstanceForPublicAPI, axiosInstanceForTenantAPI } from '~/assets/utils/axiosInstance';
import { isRESTAPIError } from '~/assets/utils/helpers';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { useAuth } from '~/contexts/auth/authContext';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import ErrorType from '~/interfaces/ErrorType';
interface TranscriptCardProps {
  title: string;
  date: string;
  onClickView: () => void;
  audioSource: string | string[];
}

function TranscriptCard({ title, date, onClickView, audioSource }: TranscriptCardProps) {

  const { reportId } = useParams();
  const { createToastNotification } = useToastNotificationContext();
  const userAccessRoles = useAppSelector(
    (state) => state.reportData.userAccessRoles,
  );
  const reportNumber = useAppSelector(
    (state) => state.reportData.report.formattedReportNumber,
  );
  const intl = useIntl();
  const { slug } = useAuth();

  const addAuditLog = async () => {
    try {
      const newdata = {
        activity: `${title}`,
      };
      const response = await axiosInstanceForTenantAPI(slug).post(
        `/report/create/download-audit-log/${reportId}`,
        newdata,
      );
      const res = response.data;
      return res;
    } catch (err) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened, We\'re currently fixing it.',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You cannot use this request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      createToastNotification(
        errorMessage,
        'error',
      );
    }
  };

  const downloadRecording = (url: string | string[]) => {
    if (!Array.isArray(url)) {
      // there should be only one recording per meeting.
      if (userAccessRoles.is_allowed_to_download_attachments) {
        try {
          axiosInstanceForPublicAPI()
            .get(url, { responseType: 'blob' })
            .then((response) => {
              // Create blob link to download
              const downloadUrl = window.URL.createObjectURL(response.data);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.setAttribute('download', `${reportNumber}-${title}`);
              // Append to html link element page
              document.body.appendChild(link);
              // Start download
              link.click();
              // Clean up and remove the link
              if (link.parentNode) link.parentNode.removeChild(link);
              if (reportId) addAuditLog();
              createToastNotification(
                intl.formatMessage({
                  defaultMessage: 'Meeting audio recording downloaded successfully.',
                }),
                'success',
              );
            })
            .catch((err: ErrorType) => {
              createToastNotification(
                'Something went wrong ' + err.message,
                'error',
              );
            });

        } catch (downloadError: unknown) {
          createToastNotification(intl.formatMessage({ defaultMessage: 'Something went wrong' }), 'error');
        }
      } else {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'You are not permitted to download the meeting audio recording on this report.',
          }),
          'info',
        );
        return;
      }
    }
    return;

  };
  return (
    <Card size='sm'>
      <div className='flex flex-row gap-1'>
        <div className='flex basis-48 items-center'>
          <Typography level='title-md'>
            {title}
          </Typography>
        </div>
        <div className='flex grow items-center'>
          <Typography startDecorator={<CalendarBlank />}>
            {dayjs(date).format('MMM D, YYYY')}
          </Typography>
        </div>
        <div className='flex flex-row items-center gap-2 text-neutral-400'>
          <Button
            color='neutral'
            variant='plain'
            onClick={() => downloadRecording(audioSource)}
          >
            <FormattedMessage defaultMessage='Download Recording' />
          </Button>
          <Button
            color='primary'
            variant='plain'
            onClick={onClickView}
          >
            <FormattedMessage defaultMessage='View' />
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default TranscriptCard;
